import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  arrayUnion,
  arrayRemove,
  doc,
  getDoc,
} from "firebase/firestore";
import { db, auth } from "../../backend/firebase";
import { ClipLoader } from "react-spinners";
import {
  FaSearch,
  FaUserPlus,
  FaUserMinus,
  FaUser,
  FaEnvelope,
  FaCheck,
  FaEye,
} from "react-icons/fa";
import "./DatasetAccess.css";

const DatasetAccess = ({ datasetid, datasetDetails }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [ownerInfo, setOwnerInfo] = useState(null);

  // Check if current user is the dataset author/admin
  const isAdmin = datasetDetails.author === auth.currentUser?.uid;

  useEffect(() => {
    if (datasetDetails.access_users) {
      fetchCollaborators(datasetDetails.access_users);
    }
    fetchOwnerInfo();
  }, [datasetDetails.access_users, datasetDetails.author]);

  const fetchOwnerInfo = async () => {
    if (!datasetDetails.author) return;

    try {
      const userRef = doc(db, "Users", datasetDetails.author);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        setOwnerInfo({
          id: datasetDetails.author,
          displayName: userData.displayName || "Unknown User",
          email: userData.email || "No email",
          profile_pic: userData.profile_pic || null,
        });
      } else {
        setOwnerInfo({
          id: datasetDetails.author,
          displayName: "Unknown User",
          email: "User not found",
          profile_pic: null,
        });
      }
    } catch (error) {
      console.error("Error fetching owner info:", error);
      setOwnerInfo({
        id: datasetDetails.author,
        displayName: "Error loading user",
        email: "Failed to load",
        profile_pic: null,
      });
    }
  };

  const fetchCollaborators = async (userIds) => {
    if (!userIds || userIds.length === 0) {
      setCollaborators([]);
      return;
    }

    setLoading(true);
    try {
      const usersRef = collection(db, "Users");

      // Fetch all users at once and filter
      const usersSnap = await getDocs(usersRef);
      const usersMap = new Map();

      usersSnap.forEach((doc) => {
        const userData = doc.data();
        usersMap.set(doc.id, {
          id: doc.id, // Use document ID as uid
          displayName: userData.displayName || "Unknown User",
          email: userData.email || "No email",
          profile_pic: userData.profile_pic || null,
        });
      });

      // Filter collaborators from the users map
      const collaboratorsData = userIds
        .map((userId) => usersMap.get(userId))
        .filter((user) => user && user.id !== datasetDetails.author); // Filter out owner

      setCollaborators(collaboratorsData);
    } catch (error) {
      console.error("Error fetching collaborators:", error);
      setError("Failed to load collaborators");
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    setError("");
    try {
      const usersRef = collection(db, "Users");
      const usersSnap = await getDocs(usersRef);

      const results = [];
      const searchLower = searchQuery.toLowerCase();

      usersSnap.forEach((doc) => {
        const userData = doc.data();
        const userId = doc.id; // Use document ID as uid

        // Skip if user is the dataset author or already a collaborator
        if (userId === datasetDetails.author || isUserCollaborator(userId)) {
          return;
        }

        const displayName = userData.displayName || "";
        const email = userData.email || "";
        const profile_pic = userData.profile_pic || null;

        // Check if search query matches displayName or email (case insensitive)
        if (
          displayName.toLowerCase().includes(searchLower) ||
          email.toLowerCase().includes(searchLower)
        ) {
          results.push({
            id: userId, // Use document ID as uid
            displayName: displayName || "Unknown User",
            email: email || "No email",
            profile_pic: profile_pic,
          });
        }
      });

      setSearchResults(results);
    } catch (error) {
      console.error("Error searching users:", error);
      setError("Failed to search users");
    } finally {
      setSearching(false);
    }
  };

  const addCollaborator = async (user) => {
    if (!isAdmin) return;

    try {
      setLoading(true);
      setError("");

      const datasetRef = doc(db, "datasets", datasetid);

      await updateDoc(datasetRef, {
        access_users: arrayUnion(user.id), // Use user.id instead of user.uid
      });

      setCollaborators((prev) => [...prev, user]);
      setSearchResults((prev) =>
        prev.filter((result) => result.id !== user.id)
      );
      setSuccess(`Added ${user.displayName} as collaborator`);

      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Error adding collaborator:", error);
      setError("Failed to add collaborator");
    } finally {
      setLoading(false);
    }
  };

  const removeCollaborator = async (user) => {
    if (!isAdmin) return;

    try {
      setLoading(true);
      setError("");

      const datasetRef = doc(db, "datasets", datasetid);

      await updateDoc(datasetRef, {
        access_users: arrayRemove(user.id), // Use user.id instead of user.uid
      });

      setCollaborators((prev) =>
        prev.filter((collab) => collab.id !== user.id)
      );
      setSuccess(`Removed ${user.displayName} from collaborators`);

      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Error removing collaborator:", error);
      setError("Failed to remove collaborator");
    } finally {
      setLoading(false);
    }
  };

  const isUserCollaborator = (userId) => {
    return collaborators.some((collab) => collab.id === userId);
  };

  return (
    <div className="dataset-access-container">
      {/* Admin-only Search Section */}
      {isAdmin && (
        <div className="dataset-access-search-section">
          <div className="dataset-access-search-box">
            <div className="dataset-access-search-input-container">
              <FaSearch className="dataset-access-search-icon" />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && searchUsers()}
                className="dataset-access-search-input"
              />
            </div>
            <button
              onClick={searchUsers}
              disabled={searching}
              className="dataset-access-search-button"
            >
              {searching ? <ClipLoader size={16} color="#ffffff" /> : "Search"}
            </button>
          </div>
          {error && <div className="dataset-access-error">{error}</div>}
          {success && <div className="dataset-access-success">{success}</div>}
          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="dataset-access-search-results">
              <h4>Search Results</h4>
              {searchResults.map((user) => (
                <div key={user.id} className="dataset-access-user-card">
                  <div className="dataset-access-user-info">
                    {user.profile_pic ? (
                      <img
                        src={user.profile_pic}
                        alt="Profile"
                        className="dataset-access-profile-pic"
                      />
                    ) : (
                      <div className="dataset-access-profile-placeholder">
                        <FaUser size={16} />
                      </div>
                    )}
                    <div className="dataset-access-user-details">
                      <div className="dataset-access-user-name">
                        {user.displayName}
                      </div>
                      <div className="dataset-access-user-email">
                        {user.email}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => addCollaborator(user)}
                    disabled={loading || isUserCollaborator(user.id)}
                    className={`dataset-access-add-button ${
                      isUserCollaborator(user.id) ? "dataset-access-added" : ""
                    }`}
                  >
                    {isUserCollaborator(user.id) ? "Added" : "Add"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Current Collaborators - Visible to Everyone */}
      <div className="dataset-access-collaborators-section">
        <div className="dataset-access-section-header">
          <h3>Current Collaborators ({collaborators.length})</h3>
          {!isAdmin && (
            <div className="dataset-access-view-only-badge">
              <FaEye size={12} />
              View Only
            </div>
          )}
        </div>

        {loading ? (
          <div className="dataset-access-loading">
            <ClipLoader size={20} color="#ffffff" />
            <span>Loading collaborators...</span>
          </div>
        ) : collaborators.length > 0 ? (
          <div className="dataset-access-collaborators-list">
            {collaborators.map((user) => (
              <div key={user.id} className="dataset-access-collaborator-card">
                <div className="dataset-access-user-info">
                  {user.profile_pic ? (
                    <img
                      src={user.profile_pic}
                      alt="Profile"
                      className="dataset-access-profile-pic"
                    />
                  ) : (
                    <div className="dataset-access-profile-placeholder">
                      <FaUser size={16} />
                    </div>
                  )}
                  <div className="dataset-access-user-details">
                    <div className="dataset-access-user-name">
                      {user.displayName}{" "}
                      <span
                        style={{
                          color: "#667eea",
                        }}
                      >
                        {user.id === auth.currentUser.uid && "(Me)"}
                      </span>
                    </div>
                    <div className="dataset-access-user-email">
                      {user.email}
                    </div>
                  </div>
                </div>
                {isAdmin && (
                  <button
                    onClick={() => removeCollaborator(user)}
                    disabled={loading}
                    className="dataset-access-remove-button"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              border: "1px dashed #333",
              borderRadius: 8,
              padding: "40px 20px",
              textAlign: "center",
              backgroundColor: "rgba(255, 255, 255, 0.02)",
            }}
            className="dataset-access-no-collaborators"
          >
            <p>No collaborators added yet.</p>
            {isAdmin && (
              <p>Search for users above to add them as collaborators.</p>
            )}
          </div>
        )}
      </div>

      {/* Dataset Owner - Visible to Everyone */}
      <div className="dataset-access-owner-section">
        <h4>Dataset Owner</h4>
        <div className="dataset-access-owner-card">
          <div className="dataset-access-user-info">
            {ownerInfo?.profile_pic ? (
              <img
                src={ownerInfo.profile_pic}
                alt="Profile"
                className="dataset-access-profile-pic"
              />
            ) : (
              <div className="dataset-access-profile-placeholder">
                <FaUser size={16} />
              </div>
            )}
            <div className="dataset-access-user-details">
              <div className="dataset-access-user-name">
                {ownerInfo?.displayName || "Loading..."}
              </div>
              <div className="dataset-access-user-email">
                {ownerInfo?.email || "Loading..."}
              </div>
            </div>
          </div>
          <div className="dataset-access-owner-badge">Owner</div>
        </div>
      </div>

      {/* Access Information for Non-Admins */}
      {!isAdmin && (
        <div className="dataset-access-info-section">
          <div className="dataset-access-info-card">
            <FaEye size={16} />
            <div className="dataset-access-info-content">
              <h4>View Only</h4>
              <p>Only dataset owners can add or remove collaborators.</p>
              <p>If you need access to this dataset, contact the owner.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatasetAccess;
